import { useEffect } from 'react';
import { useParams } from 'next/navigation';
import { usePrivate } from '@/components/private/PrivateContext';
import { buildPageTitle } from '@/lib/societyPageTitle';

/**
 * Sets the document title to "{title} | {Org Name} | BreedLOGIC", matching the
 * public-search title format.
 *
 * The `[orgName]` route segment is the organisation's short code (e.g. "AULR"),
 * so we resolve it to the full name (organisation.description, e.g. "Australian
 * Lowline Cattle Association") via the private org list. `usePrivate` returns an
 * empty context outside the private tree, so on pages without an org (e.g.
 * /account) or in public routes this safely falls back to the code or omits the
 * org entirely, leaving "{title} | BreedLOGIC".
 */
export function useDocumentTitle(title?: string) {
  const params = useParams<{ orgName?: string | string[] }>();
  const orgNameParam = params?.orgName;
  const orgCode = (
    Array.isArray(orgNameParam) ? orgNameParam[0] : orgNameParam
  )?.trim();

  const { userOrganisations } = usePrivate();
  const orgFullName = userOrganisations
    ?.find((organisation) => organisation.organisation?.name === orgCode)
    ?.organisation?.description?.trim();

  const societyName = orgFullName || orgCode;

  useEffect(() => {
    document.title = buildPageTitle({ label: title, societyName });
  }, [title, societyName]);
}
